import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './ValueTypeSelector.module.scss';
import { PriceField } from '../PriceField';
import { NumberField } from '../NumberField';
import { Label } from '../Label/Label';

export type ValueType = 'amount' | 'percentage';

export interface ValueTypeSelectorValue {
    type: ValueType;
    value: number;
}

export interface ValueTypeSelectorProps {
    id?: string;
    className?: string;
    label?: string;
    required?: boolean;
    value?: ValueTypeSelectorValue;
    defaultValue?: string;
    max?: number;
    placeholder?: string;
    disabled?: boolean;
    error?: string | null;
    onChange?: (value: ValueTypeSelectorValue) => void;
    onKeyPress?: (event: React.KeyboardEvent) => void;
}

export const ValueTypeSelector: FunctionComponent<ValueTypeSelectorProps> = ({
    id,
    className,
    label,
    required = false,
    value,
    defaultValue,
    max,
    placeholder = '0.00',
    disabled = false,
    error,
    onChange,
    onKeyPress
}) => {
    const [selectedType, setSelectedType] = useState<ValueType>(value?.type || 'amount');
    const [fieldValue, setFieldValue] = useState<number>(value?.value || 0);
    const [defaultState, setDefaultState] = useState<string | undefined>(defaultValue);
    const isInitialMount = useRef(true);

    // Sync with external value changes
    useEffect(() => {
        if (value) {
            setSelectedType(value.type);
            setFieldValue(value.value);
        }
    }, [value]);

    // Handle changes with debounce effect
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const timer = setTimeout(() => {
            setDefaultState(undefined);
            onChange?.({
                type: selectedType,
                value: fieldValue
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [fieldValue, selectedType, onChange]);

    // Handle external defaultValue changes
    useEffect(() => {
        if (defaultValue) {
            setDefaultState(defaultValue);
        }
    }, [defaultValue]);

    const handleTypeChange = (type: ValueType) => {
        setSelectedType(type);
        setFieldValue(0);
        setDefaultState(type === 'amount' ? '0.00' : '0');

        // Immediately notify parent of type change
        onChange?.({
            type,
            value: 0
        });
    };

    const handleValueChange = (newValue?: number) => {
        const value = newValue || 0;

        // For percentage type, don't allow values > 100
        if (selectedType === 'percentage' && value > 100) {
            return; // Don't update the value
        }

        // For amount type, cap at max if provided
        if (selectedType === 'amount' && max !== undefined && value > max) {
            const cappedValue = max;
            setFieldValue(cappedValue);

            // Notify parent with capped value
            onChange?.({
                type: selectedType,
                value: cappedValue
            });
            return;
        }

        setFieldValue(value);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        // Call external onKeyPress if provided
        onKeyPress?.(event);

        // If event was prevented by external handler, don't do additional validation
        if (event.defaultPrevented) {
            return;
        }

        // For percentage type, prevent entering values > 100
        if (selectedType === 'percentage') {
            const target = event.currentTarget as HTMLInputElement;
            const currentValue = target?.value || '';
            const newValue = currentValue + event.key;

            // Only validate if we have a numeric value
            if (newValue && !isNaN(Number(newValue))) {
                if (Number(newValue) > 100) {
                    event.preventDefault();
                }
            }
        }
    };

    const amountButtonClasses = clsx(styles.typeButton, {
        [styles.selected]: selectedType === 'amount'
    });

    const percentageButtonClasses = clsx(styles.typeButton, {
        [styles.selected]: selectedType === 'percentage'
    });

    const containerClasses = clsx(styles.container, className);

    return (
        <div
            id={id}
            className={containerClasses}
        >
            {label && (
                <Label
                    error={error}
                    required={required}
                >
                    {label}
                </Label>
            )}
            <div className={styles.contentContainer}>
                <div className={styles.selectorContainer}>
                    <div className={styles.typeSelector}>
                        <button
                            type="button"
                            className={amountButtonClasses}
                            onClick={() => handleTypeChange('amount')}
                            disabled={disabled}
                        >
                            <span
                                className={clsx(styles.typeText, { [styles.selectedText]: selectedType === 'amount' })}
                            >
                                $
                            </span>
                        </button>
                        <button
                            type="button"
                            className={percentageButtonClasses}
                            onClick={() => handleTypeChange('percentage')}
                            disabled={disabled}
                        >
                            <span
                                className={clsx(styles.typeText, {
                                    [styles.selectedText]: selectedType === 'percentage'
                                })}
                            >
                                %
                            </span>
                        </button>
                    </div>
                </div>

                <div className={styles.inputContainer}>
                    {selectedType === 'percentage' ? (
                        <NumberField
                            value={fieldValue}
                            default={defaultState}
                            placeholder={placeholder}
                            scale={0}
                            max={max}
                            disabled={disabled}
                            onChange={handleValueChange}
                            onKeyPress={handleKeyPress}
                            endIcon={<span className={styles.percentageSymbol}>%</span>}
                            className={styles.field}
                        />
                    ) : (
                        <PriceField
                            value={fieldValue}
                            default={defaultState}
                            placeholder={placeholder}
                            scale={2}
                            precision={8}
                            max={max}
                            disabled={disabled}
                            onChange={handleValueChange}
                            onKeyPress={handleKeyPress}
                            className={styles.field}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ValueTypeSelector;
